import { Processor } from '.';
import { Node } from '../Node';
import { Interactor, MeshRenderer } from '../component';
import { BoxDimensions } from '../../interfaces';
import { ComponentInnerState } from './Processor';
import { MeshRendererInnerState } from './MeshRendererProcessor';

interface InteractorInnerState extends ComponentInnerState {
	mesh?: string | BoxDimensions;
	isGenerated?: boolean;
	isHovered?: boolean;
	isDebugging?: boolean;
}

export class InteractorProcessor extends Processor {
	public start(component: Interactor, node: Node): boolean {
		if (!component.state.mesh) {
			return false;
		}
		const iState: InteractorInnerState = {};
		iState.mesh = component.state.mesh;

		if (typeof component.state.mesh === 'string') {
			iState.isGenerated = false;
		} else {
			iState.meshAssembly = this.engine.meshFactory.createBox(
				Object.assign({ isVisible: true }, component.state.mesh),
				'interactor'
			);
			iState.meshAssembly.visibility = 0;
			iState.isGenerated = true;
		}
		if (iState.meshAssembly && iState.isGenerated) {
			node.addChildAssembly(iState.meshAssembly);
		}

		this.setState(component, iState);

		return true;
	}

	public enable(component: Interactor, node: Node): boolean {
		const iState = this.getState<InteractorInnerState>(component);

		if (!iState.isGenerated) {
			// Trying to use the mesh renderers mesh as hit box
			const meshRenderer = node.getComponent<MeshRenderer>(component.state.mesh as string);
			if (!meshRenderer) {
				return false;
			}

			const rendererState = this.getState<MeshRendererInnerState>(meshRenderer);
			if (!rendererState) {
				return false;
			}

			if (!rendererState.meshAssembly) {
				return false;
			}
			iState.meshAssembly = rendererState.meshAssembly;
		}

		if (!iState.meshAssembly || !iState.meshAssembly.isReady() || component.isDisabled) {
			return false;
		}

		// setup listeners
		const { transformNode } = iState.meshAssembly;
		transformNode.getChildMeshes().forEach((mesh) => {
			mesh.isPickable = true;
			this.engine.pointerManager.registerPickable(mesh, node.id);

			// add callbacks
			this.engine.onScenePick.add((pickingInfo) => {
				if (pickingInfo.hit && mesh === pickingInfo.pickedMesh) {
					if (!iState.isHovered) {
						iState.isHovered = true;
						component.onPointerEnter.forEach((callback) => {
							callback({
								x: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.x : 0,
								y: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.y : 0,
								z: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.z : 0,
							});
						});
					}
				} else {
					if (iState.isHovered) {
						iState.isHovered = false;
						component.onPointerLeave.forEach((callback) => {
							callback({
								x: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.x : 0,
								y: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.y : 0,
								z: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.z : 0,
							});
						});
					}
				}
			});
		});

		return true;
	}

	public disable(component: Interactor, node: Node): boolean {
		const iState = this.getState<InteractorInnerState>(component);
		if (iState.meshAssembly) {
			const { transformNode } = iState.meshAssembly;
			transformNode.getChildMeshes().forEach((mesh) => {
				mesh.isPickable = false;
				this.engine.pointerManager.unregisterPickable(mesh, node.id);

				// remove callbacks
				this.engine.onScenePick.removeCallback((pickingInfo) => {
					if (pickingInfo.hit && mesh === pickingInfo.pickedMesh) {
						if (!iState.isHovered) {
							iState.isHovered = true;
							component.onPointerEnter.forEach((callback) => {
								callback({
									x: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.x : 0,
									y: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.y : 0,
									z: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.z : 0,
								});
							});
						}
					} else {
						if (iState.isHovered) {
							iState.isHovered = false;
							component.onPointerLeave.forEach((callback) => {
								callback({
									x: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.x : 0,
									y: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.y : 0,
									z: pickingInfo.pickedPoint ? pickingInfo.pickedPoint.z : 0,
								});
							});
						}
					}
				});
			});
			return true;
		}

		return false;
	}

	public onDestroy(component: Interactor, node: Node): boolean {
		const iState = this.getState<InteractorInnerState>(component);
		if (iState.meshAssembly && iState.isGenerated) {
			iState.meshAssembly.destroy();
		}

		// if !meshAssembly - Interactor was never enabled
		if (iState.meshAssembly) {
			const { transformNode } = iState.meshAssembly;
			transformNode.getChildMeshes().forEach((mesh) => {
				this.engine.pointerManager.unregisterPickable(mesh, node.id);
			});
		}

		this.deleteState(component);
		return true;
	}

	public updateState(component: Interactor, node: Node): boolean {
		const iState = this.getState<InteractorInnerState>(component);
		if (component.state.mesh !== iState.mesh) {
			return false;
		}

		if (iState.meshAssembly && iState.isGenerated) {
			node.addChildAssembly(iState.meshAssembly);
		} else {
			// Check if the mesh renderers assembly have changed
			const meshRenderer = node.getComponent<MeshRenderer>(component.state.mesh as string);
			if (!meshRenderer) {
				return false;
			}
			const state = this.getState(meshRenderer);
			if (iState.meshAssembly !== state.meshAssembly) {
				return false;
			}
		}

		if (this.debug !== iState.isDebugging) {
			this._debug(component);
		}

		return true;
	}

	private _debug(component: Interactor): void {
		const iState = this.getState<InteractorInnerState>(component);
		if (iState.meshAssembly) {
			iState.isDebugging = this.debug;
			const { transformNode } = iState.meshAssembly;
			transformNode.getChildMeshes().forEach((mesh) => {
				if (iState.isGenerated) {
					// So we don't accidentaly change visibility of the MeshRenderers MeshAssembly
					if (iState.isDebugging) {
						mesh.showBoundingBox = true;
						mesh.visibility = !component.isDisabled ? 0.1 : 0;
					} else {
						mesh.showBoundingBox = false;
						mesh.visibility = 0;
					}
				}
			});
		}
	}
}
